import { HttpMethod, type IHttpClient } from '@/shared/contracts/http-client.contract';
import { UIError } from '@/shared/utils/ui-error';

export type UseCaseInput = {
  coupon: string;
  subtotal: number;
  storeSlug: string;
  /** PRAT-218: linhas do carrinho para cupom de produtos/categorias (desconto só sobre elas). */
  items?: { item_id: string; category_id?: string | null; amount: number }[];
};

type BackendResponse = {
  id: string;
  discount_type: string;
  discount_value: number;
  subtotal_with_discount: number;
  free_delivery: boolean;
};

export type UseCaseOutput = {
  id: string;
  discount_type: 'PERCENTAGE' | 'AMOUNT' | 'DELIVERY_FREE';
  discount_value: number;
  subtotal_with_discount: number;
  free_delivery: boolean;
};

export class RedeemCupomUseCase {
  private client: IHttpClient;

  constructor(client: IHttpClient) {
    this.client = client;
  }

  async execute(input: UseCaseInput): Promise<UseCaseOutput> {
    const { coupon, subtotal, storeSlug, items } = input;

    if (!coupon) {
      throw new UIError('COUPON_REQUIRED', 'Cupom é obrigatório');
    }

    if (!storeSlug) {
      throw new UIError('STORE_REQUIRED', 'Loja é obrigatória');
    }

    if (subtotal < 0) {
      throw new UIError('INVALID_SUBTOTAL', 'Subtotal inválido');
    }

    const response = await this.client.sendRequest<BackendResponse, never>({
      endpoint: `api/app/merchants/${storeSlug}/checkout/coupons/${coupon}`,
      method: HttpMethod.GET,
      params: {
        subtotal,
        ...(items?.length ? { items: JSON.stringify(items) } : {}),
      },
    });

    // Calculate the actual discount amount from backend response
    const backendDiscountAmount = subtotal - response.subtotal_with_discount;

    // Ensure the discount doesn't exceed the subtotal (prevent negative values)
    // Priority: max_discount from coupon rules > subtotal limit > backend calculation
    const actualDiscountAmount = Math.min(backendDiscountAmount, subtotal);
    const finalSubtotalWithDiscount = Math.max(subtotal - actualDiscountAmount, 0);

    // Map backend response to expected format with validation
    return {
      id: response.id,
      discount_type: response.discount_type as UseCaseOutput['discount_type'],
      discount_value: response.discount_value,
      subtotal_with_discount: finalSubtotalWithDiscount,
      free_delivery: response.free_delivery,
    };
  }
}
